import { ARENA_WIDTH } from './asteroid-field';
import { NO_INPUT, type MoveIntent } from './racer-movement';

/**
 * Touch steering, as a pure rule.
 *
 * See docs/touch-controls.md. The short version: a floating thumbstick on the
 * left half of the arena, boost on the right, and the stick quantises to the
 * same eight directions a keyboard offers. Quantising keeps touch and keyboard
 * players on one high score board, leaves the physics untouched, and keeps a
 * player's intent at five bits for when it has to cross a wire.
 */

/** Where a thumb was planted, and where it has been dragged to. */
export interface StickReading {
  originX: number;
  originY: number;
  x: number;
  y: number;
}

/** Inside this, the thumb is resting rather than steering. */
export const STICK_DEAD_ZONE = 18;

/** The throw of the stick — a thumb pivoting without regripping. */
export const STICK_FULL_DEFLECTION = 72;

/** Which thumb a touch at this x belongs to. */
export function touchZone(x: number): 'stick' | 'boost' {
  return x < ARENA_WIDTH / 2 ? 'stick' : 'boost';
}

/**
 * The knob's offset from the stick origin, clamped to the stick's throw.
 *
 * Rendering only — the intent above does not care how far past the edge a
 * thumb has slid, but the player needs to see the stick stop.
 */
export function stickOffset(reading: StickReading): { x: number; y: number } {
  const dx = reading.x - reading.originX;
  const dy = reading.y - reading.originY;
  const distance = Math.hypot(dx, dy);

  if (distance <= STICK_FULL_DEFLECTION || distance === 0) {
    return { x: dx, y: dy };
  }

  const scale = STICK_FULL_DEFLECTION / distance;
  return { x: dx * scale, y: dy * scale };
}

/**
 * The eight directions, indexed by 45° wedge starting at "due right" and
 * turning clockwise — which is downwards first, because screen y grows down.
 */
const WEDGES: ReadonlyArray<Partial<MoveIntent>> = [
  { right: true },
  { right: true, down: true },
  { down: true },
  { left: true, down: true },
  { left: true },
  { left: true, up: true },
  { up: true },
  { right: true, up: true },
];

/**
 * Combines two control schemes into one intent.
 *
 * Touch is additive rather than exclusive, so a tablet with a keyboard can
 * switch between them mid-round without a mode to toggle. Opposing directions
 * from different schemes are left to cancel out in `advanceBody`, which is
 * already what happens when one hand holds both arrow keys.
 */
export function mergeIntent(a: MoveIntent, b: MoveIntent): MoveIntent {
  return {
    up: a.up || b.up,
    down: a.down || b.down,
    left: a.left || b.left,
    right: a.right || b.right,
    boost: a.boost || b.boost,
  };
}

/** Turns a thumb position and a boost press into the intent the race reducer takes. */
export function touchIntent(stick: StickReading | null, boosting: boolean): MoveIntent {
  const intent: MoveIntent = { ...NO_INPUT, boost: boosting };

  if (!stick) {
    return intent;
  }

  const dx = stick.x - stick.originX;
  const dy = stick.y - stick.originY;

  if (Math.hypot(dx, dy) < STICK_DEAD_ZONE) {
    return intent;
  }

  const wedge = Math.round(Math.atan2(dy, dx) / (Math.PI / 4));
  return { ...intent, ...WEDGES[((wedge % 8) + 8) % 8] };
}
